import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldX } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

interface RequireOrgMembershipProps {
  orgId: string;
  children: ReactNode;
}

/**
 * Bloqueia o acesso se o usuário NÃO for membro ativo da org informada.
 * Diferente de OrgRestrictedRoute, não depende da org "selecionada" no contexto —
 * checa diretamente em memberships. Útil para páginas admin que devem funcionar
 * independente da org que o usuário está navegando no momento.
 */
export function RequireOrgMembership({ orgId, children }: RequireOrgMembershipProps) {
  const navigate = useNavigate();
  const [state, setState] = useState<'loading' | 'allowed' | 'denied'>('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth.user?.id;
      if (!userId) {
        if (!cancelled) setState('denied');
        return;
      }
      const { data, error } = await supabase
        .from('memberships')
        .select('user_id')
        .eq('user_id', userId)
        .eq('org_id', orgId)
        .eq('status', 'active')
        .maybeSingle();
      if (cancelled) return;
      setState(!error && data ? 'allowed' : 'denied');
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  if (state === 'loading') {
    return (
      <div className="flex min-h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        <span className="ml-2 text-muted-foreground">Verificando permissões…</span>
      </div>
    );
  }

  if (state === 'denied') {
    return (
      <div className="container mx-auto px-6 py-8">
        <Alert variant="destructive">
          <ShieldX className="h-4 w-4" />
          <AlertDescription>
            Você não tem permissão para acessar esta página. É necessário ser membro ativo da organização.
          </AlertDescription>
        </Alert>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard')}>
          Voltar ao Dashboard
        </Button>
      </div>
    );
  }

  return <>{children}</>;
}
